import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Lock } from "lucide-react";
import { hasSuperAdminAccess } from "../../utils/roleAccess";
import useMyTeam from "../../hooks/useMyTeam";

/**
 * Restricts a route to tech / super-admin accounts (super_admin + dev) - the
 * same audience as roleAccess.hasSuperAdminAccess, which deliberately excludes
 * plain admins and agents.
 *
 * Used for Support Tickets (CRM-2026-00034): George asked to keep that tool for
 * tech/super admins only. Hiding the sidebar row is not enough on its own - the
 * route is reachable by direct URL - so it is guarded here too, mirroring the
 * ComingSoonRoute pattern but keyed on super-admin access with a role-blocked
 * (not "coming soon") message.
 */
const SuperAdminRoute = ({ children, title = "This page", allowTeam = false, message = "" }) => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { hasTeam, loaded } = useMyTeam();

  if (hasSuperAdminAccess(user)) {
    return children;
  }
  // allowTeam: a team lead may open their own team's pages too - Agent
  // Performance (Christian Gravias, Monday 13225574145). The backend still
  // limits them to their team; this only decides whether the page opens.
  if (allowTeam && !loaded) {
    return null;
  }
  if (allowTeam && hasTeam) {
    return children;
  }

  return (
    <div className="flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full text-center bg-white border border-gray-100 rounded-2xl shadow-sm p-8">
        <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
          <Lock className="w-6 h-6 text-gray-500" />
        </div>
        <h1 className="mt-4 text-lg font-semibold text-gray-900">
          {title} isn't available for your role
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          {message ||
            (allowTeam
              ? "This page is for super admins and for agents with a team."
              : "Support Tickets are handled by the tech team. To flag a CRM problem, use Report an Issue instead.")}
        </p>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-6 px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ backgroundColor: "#55C5D0" }}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default SuperAdminRoute;
